"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { kasaSizesApi, securityDoorColorsApi, type SettingRecord } from "@/services/api.ts"

type SecurityDoorSettings = {
  kasaSizes: SettingRecord[]
  doorColors: SettingRecord[]
  loading: boolean
}

const SecurityDoorSettingsContext = createContext<SecurityDoorSettings>({
  kasaSizes: [],
  doorColors: [],
  loading: true,
})

export function SecurityDoorSettingsProvider({ children }: { children: ReactNode }) {
  const [kasaSizes, setKasaSizes] = useState<SettingRecord[]>(() => kasaSizesApi.cached() ?? [])
  const [doorColors, setDoorColors] = useState<SettingRecord[]>(() => securityDoorColorsApi.cached() ?? [])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      kasaSizesApi.getAll().then((data) => {
        if (!cancelled) {
          setKasaSizes(data)
          kasaSizesApi.cache(data)
        }
      }),
      securityDoorColorsApi.getAll().then((data) => {
        if (!cancelled) {
          setDoorColors(data)
          securityDoorColorsApi.cache(data)
        }
      }),
    ])
      .catch(() => {
        // backend may not be connected
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <SecurityDoorSettingsContext.Provider value={{ kasaSizes, doorColors, loading }}>
      {children}
    </SecurityDoorSettingsContext.Provider>
  )
}

export function useSecurityDoorSettings() {
  return useContext(SecurityDoorSettingsContext)
}
